<template>
  <span class="checkpoint-history-tooltip">
    <!-- Trigger (e.g. the history clock icon button) is provided by the consumer. -->
    <slot />
    <span
      class="checkpoint-history-tooltip__bubble"
      role="tooltip"
      data-testid="checkpoint-history-tooltip-bubble"
    >
      <template v-if="showWhatChanged">
        <span class="checkpoint-history-tooltip__heading">
          This is what changed from the previous version
        </span>
        <span class="checkpoint-history-tooltip__message">{{ publishMessage }}</span>
      </template>
      <span class="checkpoint-history-tooltip__cta">Click to open history of versions</span>
    </span>
  </span>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  /**
   * Current published version number (from the version-history endpoint).
   * Used to decide whether there is a prior version to diff against (v2+).
   */
  versionNumber: {
    type: [Number, String],
    default: null,
  },
  /**
   * The resolved publish_message text (Rule 12). Only surfaced when
   * `publishMessageStatus` is "READY".
   */
  publishMessage: {
    type: String,
    default: null,
  },
  /**
   * Rule 12 `publish_message_status` enum value:
   * NOT_APPLICABLE | GENERATING | READY | FAILED.
   */
  publishMessageStatus: {
    type: String,
    default: null,
  },
});

/**
 * The "what changed" content is shown only for v2+ versions whose message has
 * resolved to READY. Every other case (v1, GENERATING, FAILED, or missing data)
 * collapses to the single "Click to open history of versions" hint.
 */
const showWhatChanged = computed(() => {
  const version = Number(props.versionNumber);
  return Number.isFinite(version) && version >= 2 && props.publishMessageStatus === "READY";
});
</script>

<style scoped>
.checkpoint-history-tooltip {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.checkpoint-history-tooltip__bubble {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 240px;
  max-width: 320px;
  padding: 12px;
  background-color: #0c111d;
  border: 1px solid #303133;
  border-radius: 8px;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.15s ease, visibility 0.15s ease;
}

.checkpoint-history-tooltip:hover .checkpoint-history-tooltip__bubble,
.checkpoint-history-tooltip:focus-within .checkpoint-history-tooltip__bubble {
  opacity: 1;
  visibility: visible;
}

.checkpoint-history-tooltip__heading,
.checkpoint-history-tooltip__message,
.checkpoint-history-tooltip__cta {
  font-family: Inter, sans-serif;
  font-size: 12px;
  line-height: 18px;
  word-wrap: break-word;
}

.checkpoint-history-tooltip__heading {
  color: #ffffff;
  font-weight: 600;
}

.checkpoint-history-tooltip__message {
  color: #d0d5dd;
  font-weight: 500;
  white-space: pre-wrap;
  word-break: break-word;
}

.checkpoint-history-tooltip__cta {
  color: #d0d5dd;
  font-weight: 500;
}
</style>
